import { useEffect } from 'react';

/**
 * Holds the left navigation collapsed while the calling screen is mounted, through the shell's
 * shared `ai:nav-collapse` / `ai:nav-restore` events (AppShell remembers the state before the
 * collapse and puts it back).
 *
 * Holders are COUNTED, so screens nested inside one another compose: the navigation collapses
 * when the first holder mounts and comes back only when the LAST one unmounts. A plain
 * collapse/restore pair per screen does not nest — the inner screen's restore reopened the
 * navigation while the outer one was still open (PLM: leaving a batch for the PLM list, or
 * closing a Daily QC read opened from a batch, owner report 2026-11-03).
 *
 * Every holder sends the collapse when it mounts, so a nested screen folds the navigation away
 * again if the user had reopened it by hand. The collapse is sent again on the next frame: on a direct load the screen and the shell mount
 * in the same commit, child effects run first, and the first event arrives before the shell
 * listens. Collapsing twice is harmless — the shell records the earlier state only once.
 */
let holders = 0;

export function useNavCollapse(enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    holders += 1;
    // Every holder collapses on mount (a nested screen opened after the user reopened the
    // navigation by hand folds it away again); only the last one restores.
    window.dispatchEvent(new Event('ai:nav-collapse'));
    const raf = requestAnimationFrame(() => window.dispatchEvent(new Event('ai:nav-collapse')));
    return () => {
      cancelAnimationFrame(raf);
      holders = Math.max(0, holders - 1);
      if (holders === 0) window.dispatchEvent(new Event('ai:nav-restore'));
    };
  }, [enabled]);
}

/** Test seam: the current number of holders. */
export function navCollapseHolders(): number {
  return holders;
}
